import React, { useState, useEffect } from 'react';

const Solutions = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Farmnet helps farmers optimize water use—boosting yields while conserving precious water resources through smart digital tools.";

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setIsTyping(false);
          setTimeout(() => {
            backspaceText();
          }, 2000);
        }, 1000);
      }
    };

    const backspaceText = () => {
      let backspaceInterval;
      let backspaceIndex = fullText.length;

      const eraseText = () => {
        if (backspaceIndex >= 0) {
          setText(fullText.substring(0, backspaceIndex));
          backspaceIndex--;
        } else {
          clearInterval(backspaceInterval);
          setIsTyping(true);
          currentIndex = 0;
          typingInterval = setInterval(typeText, 50);
        }
      };

      backspaceInterval = setInterval(eraseText, 20);
    };

    if (isTyping) {
      typingInterval = setInterval(typeText, 50);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isTyping, fullText]);

  return (
    <div className="relative bg-[#F3F3F3]">
      {/* Content */}
      <div className="relative z-10 px-4 md:px-24 pb-10 md:pt-5 pt-1">
        {/* Heading */}
        <div className="mt-2 md:mt-2 md:mb-5 flex justify-between items-center p mb-2">
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 ">
          <span className='text-[#152C24]'>INSIGHTS & INNOVATIONS</span>
          </h1>
          <div>
            <div className='flex md:gap-2 gap-1 justify-center md:mt-2'>
              <div className='bg-[#CFD7D7] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
              <div className="bg-[#2E453D] border-b-2 border-[#FFBD26] md:ml-0 ml-2 text-white font-normal md:px-6 px-2 rounded-md md:rounded-[30px] w-[70px] h-[15px] md:w-[150px] md:text-[20px] text-[6px] md:h-[40px] mt-0 md:-mt-[1px] flex justify-between items-center">
                See More
              </div>
              <div className='bg-[#2E453D] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 mr-2 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
            </div>
          </div>
        </div>

        {/* Records Section */}
        <div className="rounded-md w-full md:h-[630px] h-[175px] relative flex items-center justify-center p-2 md:p-0 pt-2 md:mt-4">
          <div className="rounded-xl overflow-hidden relative w-full h-[180px] md:mt-5 md:h-[700px] flex justify-center items-center cursor-pointer">
              <>
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751380763/image-4_y39arg.png"
                  alt="Mobile"
                  className="object-cover w-full h-full"
                />
              </>
          </div>
        </div>

        {/* Description */}
        <div className='md:text-[32px] text-[10px] font-normal md:mt-16 mt-2 space-y-6'>
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 text-[#979797]">
            INSIGHTS & INNOVATIONS | <span className='text-[#152C24]'>Water Management</span>
          </h1>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            💧 What Is Digital Water Management?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Digital water management refers to the use of digital technologies such as sensors, mobile apps, remote monitoring systems, and data analytics to efficiently monitor, manage, and optimize water use in agriculture. Its goal is to help farmers use water wisely, reduce waste, and improve crop productivity.
          </p>
          
          <p className="md:text-[32px] text-[8px]">
            These systems track things like:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Soil moisture levels</li>
            <li>Water availability (from boreholes, rivers, rainwater)</li>
            <li>Irrigation schedules and usage</li>
            <li>Weather forecasts to plan watering</li>
            <li>Water-related crop performance data</li>
          </ul>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🌍 How Smallholder Farmers in Africa Use Digital Water Management
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Smallholder farmers in Africa often operate in water-scarce and rain-dependent environments, so managing water efficiently is critical. While full-scale systems may be costly, many low-cost, mobile-based or solar-powered solutions are increasingly accessible to smallholders. Here's how they're being used:
          </p>
          
          <ol className="md:text-[32px] text-[8px] list-decimal pl-5 space-y-3 [&>li::marker]:font-bold">
            <li>
              <span className="font-bold">Soil Moisture Sensors & Alerts</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Affordable soil sensors measure moisture levels in the field</li>
                <li>Send data via SMS or app to indicate when to irrigate</li>
                <li>Prevents over- or under-watering crops</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Mobile Weather Forecasting Tools</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use apps or SMS services to predict rainfall</li>
                <li>Helps plan irrigation or planting schedules</li>
                <li>Avoids watering right before rain or during drought</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Smart Irrigation Systems</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Solar-powered drip irrigation and gravity-fed systems</li>
                <li>Connected to digital timers or moisture sensors</li>
                <li>Automates water delivery based on need</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Water Usage Monitoring Apps</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Help farmers track daily water use</li>
                <li>Compare with crop water needs</li>
                <li>Some include seasonal water budgeting features</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Remote Monitoring of Water Sources</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Community boreholes or tanks fitted with digital sensors</li>
                <li>Farmers know when water is available</li>
                <li>Monitors pump usage and maintenance needs</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Decision Support from Platforms Like Farmnet</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Provides irrigation scheduling tools based on crop type</li>
                <li>Weather-linked watering advice</li>
                <li>Training on conservation techniques (mulching, water harvesting)</li>
              </ul>
            </li>
          </ol>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            ✅ Benefits for Smallholder Farmers
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Digital water management helps farmers to:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Save water and lower irrigation costs</li>
            <li>Grow more with less, especially in dry seasons</li>
            <li>Improve crop yields and resilience</li>
            <li>Plan better with reliable data</li>
            <li>Adapt to climate change and unpredictable rainfall patterns</li>
          </ul>
        </div>
        
        <div className='flex justify-end items-center'>
          <div>
            <h1 className="text-[18px] md:text-[32px] font-bold mt-2 mb-4 text-black">
              Download App
            </h1>
            <div className="md:w-[450px]">
              <div className="flex flex-row justify-between space-x-2 items-center sm:space-y-0">
                <a href="https://play.google.com/store/apps/details?id=com.farmnet.africa&pcampaignid=web_share" alt="none">   
             <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751889690/google_PLAY-LOGO.WINE_emyxpu.png" className="h-[25px] md:w-[280px] md:h-[180px]" alt="App Store" />    
             <p className="md:text-[14px] text-[6px] md:-mt-12 md:pl-5 font-normal md:px-1">
             Click to download App
  </p>
     </a>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744414874/image_scynki.png"
                  alt="Google Play"
                  className="h-[30px] md:h-[75px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
        <div className='md:h-10 h-5'></div>
      </div>
    </div>
  );
};

export default Solutions;